import { List, Record } from "immutable";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import {
  CONTEXT_BAR_HEIGHT,
  RELATIONSHIP_HEADER_HEIGHT,
  SPLIT_DIVIDER_SIZE,
  TIMELINE_HEADER_HEIGHT
} from "../../constants";
import { FILTER_LABELS } from "../../constants/filter";
import {
  COLLAPSED_INSPECTOR_PANEL,
  GLOBAL_FILTER_ID,
  ITEM_INSPECTOR_PANEL,
  LAYOUT_VIEW,
  MARKER_INSPECTOR_PANEL,
  MINDMAP_VIEW,
  MIXED_VIEWS,
  NARRATIVE_VIEW,
  ORIENTATION_COLUMN,
  ORIENTATION_ROW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SCROLL_TO_DATE_PANEL,
  SETTINGS_PANEL,
  SPREADSHEET_VIEW,
  SUBWAY_VIEW,
  SYNC_BINDER_PANEL,
  SYNC_INSPECTOR_PANEL,
  TIMELINE_VIEW,
  TUTORIAL_PANEL
} from "../../constants/identifiers";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import sortOptions from "../../constants/sortOptions";
import Dimensions from "../../constants/styles/dimensions";
import suggestionLibrary, { suggestionLibraryValues } from "../../suggestionLibrary";
import { createFromJS, immutableRecords } from "../../types";
import filterUtils, { getFilterSubLabelByPropName } from "../../utils/filter";
import { mapValuesToArray } from "../../utils/immutableExtensions";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { definitionSelectors } from "../definition";
import { calendarSelectors } from "../definition/calendar";
import { unitScaleForZoomLimit } from "../definition/calendar/dateFunctions/unitScaleFunctions";
import { colorSelectors } from "../definition/colors";
import { propertySelectors } from "../definition/properties";
import { referenceSelectors } from "../definition/references";
import { typeSelectors } from "../definition/types";
import { filterSelectors } from "../filters";
import { itemSelectors } from "../items";
import { narrativeSelectors } from "../narrative";
import { savedFilterSelectors } from "../savedFilters";
import { tagSelectors } from "../tags";
import { getExpandedItemKeyForMobile } from "./reducers/utils";
import { getActiveViewId as getActiveViewIdDesktop, getOpenViews as getOpenViewsDesktop } from "./selectors.desktop";
import {
  getActiveViewId as getActiveViewIdMobile,
  getMobilePortraitViewType as getMobilePortraitViewTypeMobile,
  getOpenViews as getOpenViewsMobile
} from "./selectors.mobile";
import { getActiveViewId as getActiveViewIdTablet, getOpenViews as getOpenViewsTablet } from "./selectors.tablet";

let getOpenViews = null;
let getActiveViewId = null;
let getMobilePortraitViewType = null;

// #!if mobile === true
// getOpenViews = getOpenViewsMobile;
// getActiveViewId = getActiveViewIdMobile;
// getMobilePortraitViewType = getMobilePortraitViewTypeMobile;
// #!elseif tablet === true
// getOpenViews = getOpenViewsTablet;
// getActiveViewId = getActiveViewIdTablet;
// #!else
getOpenViews = getOpenViewsDesktop;
getActiveViewId = getActiveViewIdDesktop;
// #!endif
getMobilePortraitViewType = getMobilePortraitViewTypeMobile;
// #!if harvard === true
// #!endif

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();
const isHarvard = preprocessorQuery.isHarvard();

const canViewTypeSyncScrolling = viewType =>
  viewType === TIMELINE_VIEW || viewType === RELATIONSHIP_VIEW || viewType === NARRATIVE_VIEW;

const getSelectedItems = state => state.getIn(["appState", "selectedItems"]);
const getSelectedMarkers = state => state.getIn(["appState", "selectedMarkers"]);
const getSelectedSyncItems = state => state.getIn(["appState", "selectedSyncItems"]);

const getSelectedItemCount = state => getSelectedItems(state).size;
const getSelectedMarkerCount = state => getSelectedMarkers(state).size;
const getSelectedSyncItemCount = state => getSelectedSyncItems(state).size;

const getSelectedItemsAreUnlocked = state => {
  const items = getSelectedItems(state).map(id => itemSelectors.getItemFromId(state, id));
  if (!items.size) {
    return false;
  }
  return items.every(item => !item.isDateLocked);
};

const getSelectedItemsShareSuper = state => {
  const items = getSelectedItems(state).map(id => itemSelectors.getItemFromId(state, id));
  if (!items.size) {
    return false;
  }
  const firstSuper = items.get(0).super;
  return items.every(item => item.super === firstSuper);
};

const getSelectedItemsShareNarrativeParent = state => {
  const selectedIds = getSelectedItems(state);
  const narrativeParents = narrativeSelectors
    .getAllNarrativeParentsByIdFromState(state)
    .filter((parent, id) => selectedIds.indexOf(id) >= 0);
  const uniqueParents = narrativeParents.toList().toSet();
  if (uniqueParents.size > 1) {
    return false;
  }
  return true;
};

const getEarliestSharedNarrativePositionForSelectedItems = state => {
  const selectedIds = getSelectedItems(state);
  const positions = narrativeSelectors.getParentAndIndexForNarrativeIds(state, selectedIds);
  // Find the position with the earliest index
  return positions.minBy(obj => obj.index);
};

const getDesktopLeftPanelMode = state => state.appState.leftPanelMode;
const getTabletPanelMode = state => state.appState.tabletPanelMode;
const getMobilePanelMode = state => state.appState.mobilePanelMode;

const getLeftPanelMode = state => {
  let leftPanelMode = isDesktop
    ? getDesktopLeftPanelMode(state)
    : isTablet
      ? getTabletPanelMode(state)
      : isMobile
        ? getMobilePanelMode(state)
        : null;
  // When no item selected, do not show item inspector
  if (leftPanelMode === ITEM_INSPECTOR_PANEL && state.appState.selectedItems.size === 0) {
    leftPanelMode = null;
  }
  // In desktop mode, should not show the iOS modes
  if (isDesktop && !isHarvard && (leftPanelMode === SCROLL_TO_DATE_PANEL || leftPanelMode === SETTINGS_PANEL)) {
    leftPanelMode = null;
  }
  return leftPanelMode;
};
const getShowRightPanel = state => state.appState.rightPanelMode !== COLLAPSED_INSPECTOR_PANEL;
const getLeftPanelWidth = state => state.appState.leftPanelWidth;
const getRightPanelWidth = state => state.appState.rightPanelWidth;

const getDesktopVisibleTypeListId = state => state.appState.visibleTypeList;
const getTabletVisibleTypeListId = state => state.appState.tabletVisibleTypeList;
const getMobileVisibleTypeListId = state => state.appState.mobileVisibleTypeList;

const getVisibleTypeListId = state =>
  isDesktop
    ? getDesktopVisibleTypeListId(state)
    : isTablet
      ? getTabletVisibleTypeListId(state)
      : isMobile
        ? getMobileVisibleTypeListId(state)
        : null;

const getRightPanelMode = state => {
  let rightPanelMode = null;

  //#!if iOS === true
// rightPanelMode = state.appState.rightPanelMode;
  // When no item is selected, do not show item inspector
// if (rightPanelMode === ITEM_INSPECTOR_PANEL && state.appState.selectedItems.size === 0) {
// rightPanelMode = null;
// } else if (rightPanelMode === TUTORIAL_PANEL) {
// rightPanelMode = null;
// } else if (rightPanelMode === MARKER_INSPECTOR_PANEL && state.appState.selectedMarkers.size === 0) {
// rightPanelMode = null;
// }
// 
  //#!else
  rightPanelMode = state.appState.rightPanelMode
    ? state.appState.rightPanelMode
    : getSelectedMarkerCount(state)
      ? MARKER_INSPECTOR_PANEL
      : getSelectedSyncItemCount(state)
        ? SYNC_INSPECTOR_PANEL
        : ITEM_INSPECTOR_PANEL;
  //#!endif

  return rightPanelMode;
};

const isItemInspectorOpen = state =>
  getRightPanelWidth(state) >= Dimensions.MIN_RIGHT_PANEL_WIDTH && getRightPanelMode(state) === ITEM_INSPECTOR_PANEL;

const isSyncTabOpen = state => getLeftPanelMode(state) === SYNC_BINDER_PANEL;

const getInspectorTab = state => state.appState.inspectorTab;
const getNotificationTab = state => state.appState.notificationTab;
const getSyncTab = state => state.appState.syncTab;

const getOpenViewsById = state => getOpenViews(state).get("byId");
const getOpenViewCount = state => getOpenViews(state).byId.size;
const getRootLayout = state => getOpenViews(state).get("layout");

const isViewActive = (state, viewId) => getActiveViewId(state) === viewId;

const getScrollingIsLocked = state => state.getIn(["appState", "scrollingIsLocked"]);

const getIgnoredNotifications = state => state.getIn(["appState", "ignoredNotifications"]);

const getDisableFiltering = state => state.getIn(["appState", "disableFiltering"]);

const getDisableFilteringMore = state => state.getIn(["appState", "disableFilteringMore"]);

const getDisableSplitView = state => state.getIn(["appState", "disableSplitView"]);

const getDisableTimelineZoom = state => state.getIn(["appState", "disableTimelineZoom"]);

const getExposedFilterTypes = state => state.getIn(["appState", "exposedFilterTypes"]);

const getViewFilter = (state, viewId) => filterSelectors.getFilterWithId(state, viewId);

const getGlobalFilter = state => filterSelectors.getFilterWithId(state, GLOBAL_FILTER_ID);

const getViewGroupLabel = (state, viewId) => {
  const view = getOpenViewsById(state).get(viewId);
  if (!view) {
    return "All Events";
  }

  const viewType = view.get("viewType");

  if (viewType === MINDMAP_VIEW) {
    const mindmapId = view.getIn(["mindmapOptions", "mindmapId"]);
    return state.getIn(["data", "mindmaps", "byId", mindmapId, "label"]);
  } else {
    let viewLabel = "";
    switch (viewType) {
      case TIMELINE_VIEW:
        viewLabel = "Timeline";
        break;
      case SUBWAY_VIEW:
        viewLabel = "Subway";
        break;
      case SPREADSHEET_VIEW:
        viewLabel = "Spreadsheet";
        break;
      case RELATIONSHIP_VIEW:
        viewLabel = "Relationship";
        break;
      case NARRATIVE_VIEW:
        viewLabel = "Narrative";
        break;
      case OUTLINE_VIEW:
        viewLabel = "Narrative Outline";
        break;
    }

    const label = `${viewLabel}`;
    return label;
  }
};

const getActiveFilterNameForViewId = (state, viewId) => {
  const filters = filterSelectors.getFiltersById(state);
  const savedNames = savedFilterSelectors.getSavedFilters(state);
  const activeFilter = filterSelectors.getFilterWithId(state, viewId);
  if (!activeFilter) {
    return null;
  }
  const filterKey = filters.findKey((f, id) => f === activeFilter && savedNames.has(id));
  return filterKey ? savedNames.get(filterKey) : null;
};

const getViewFilterLabel = (state, viewId) => {
  const activeFilterName = getActiveFilterNameForViewId(state, viewId);
  // Show the saved filter name
  if (activeFilterName) {
    return activeFilterName;
  }
  // Show filter items for the view
  else {
    const filter = filterSelectors.getFilterWithId(state, viewId);
    let resultArray = "";
    if (filter) {
      const filterPropNames = ["label", "duration", "summary"];
      resultArray = filterPropNames.map(name =>
        getFilterSubLabelByPropName(filter[name], name, true, FILTER_LABELS[name])
      );
      // tags
      const tags = tagSelectors.getTags(state);
      resultArray.push(
        filterUtils.getTagsFilterSubLabel(filter.tags, tags, !(filter.tags.terms.size > 1), FILTER_LABELS.tags)
      );
      // color
      const colors = colorSelectors.getColors(state);
      resultArray.push(
        filterUtils.getListFilterSubLabel(filter.color, colors, !(filter.color.terms.size > 1), FILTER_LABELS.color)
      );
      // type
      const types = typeSelectors.getOrderedTypes(state);
      resultArray.push(
        filterUtils.getListFilterSubLabel(filter.type, types, !(filter.type.terms.size > 1), FILTER_LABELS.type)
      );
      // date
      resultArray.push(
        filterUtils.getDateFilterSubLabel(filter.date, calendarSelectors.getCalendar(state), FILTER_LABELS.date)
      );

      const typesById = typeSelectors.getTypesById(state);

      // relationship by type
      resultArray = resultArray.concat(
        mapValuesToArray(
          filter.relationshipsByType.map((filterPart, typeId) =>
            filterUtils.getListFilterSubLabel(
              filterPart,
              itemSelectors.getItemsOfType(state, typeId),
              !(filterPart.terms.size > 1),
              typesById.get(typeId).label
            )
          )
        )
      );
      // relationship by reference
      const referencesById = referenceSelectors.getReferencesById(state);
      resultArray = resultArray.concat(
        mapValuesToArray(
          filter.relationshipsByReference.map((filterPart, referenceId) =>
            filterUtils.getListFilterSubLabel(
              filterPart,
              itemSelectors.getItemsOfTypes(state, referencesById.get(referenceId).types),
              !(filterPart.terms.size > 1),
              referencesById.get(referenceId).label
            )
          )
        )
      );

      const propertiesById = propertySelectors.getPropertiesById(state);
      // text props
      resultArray = resultArray.concat(
        mapValuesToArray(
          filter.textProperties.map((filterPart, propertyId) => {
            const property = propertiesById.get(propertyId);
            if (property.format === PropertyFormatIdentifier.list) {
              return filterUtils.getListFilterSubLabel(filterPart, property.allowed, true, property.label);
            }
            return filterUtils.getStringFilterSubLabel(filterPart, true, property.label);
          })
        )
      );
      // numeric props
      resultArray = resultArray.concat(
        mapValuesToArray(
          filter.numericProperties.map((filterPart, propertyId) =>
            filterUtils.getNumericFilterSubLabel(filterPart, propertiesById.get(propertyId).label)
          )
        )
      );
      // bool props
      resultArray = resultArray.concat(
        mapValuesToArray(
          filter.boolProperties.map((filterPart, propertyId) =>
            filterUtils.getBooleanFilterSubLabel(filterPart, propertiesById.get(propertyId).label)
          )
        )
      );
    }
    return resultArray.filter(str => str).join(" | ");
  }
};

const getViewLabel = (state, viewId) => {
  let label;
  //#!if harvard === true
  const isGlobalFilterActive = filterSelectors.isFilterWithIdActive(state, GLOBAL_FILTER_ID);
  const globalFilterLabel = getViewFilterLabel(state, GLOBAL_FILTER_ID);
  const isFilterActive = filterSelectors.isFilterWithIdActive(state, viewId);
  const filterLabel = getViewFilterLabel(state, viewId);
  label = isGlobalFilterActive ? globalFilterLabel : isFilterActive ? filterLabel : "All Events";
  //#!else
// const viewGroupLabel = getViewGroupLabel(state, viewId);
// label = `${viewGroupLabel}`;
  //#!endif
  return label;
};

const getViewSubLabel = (state, viewId) => {
  const isFilterActive = filterSelectors.isFilterWithIdActive(state, viewId);
  const filterLabel = getViewFilterLabel(state, viewId);

  let label;
  //#!if harvard !== true
// label = isFilterActive ? `${filterLabel}` : "";
  //#!endif
  return label;
};

const getViewLabelColor = (state, viewId) => {
  //#!if harvard !== true
// const view = getOpenViewsById(state).get(viewId);
// if (!view) {
// return "labelChronological";
// }
// const viewType = view.get("viewType");
// if (viewType === NARRATIVE_VIEW || viewType === OUTLINE_VIEW) {
// return "labelNarrative";
// }
  //#!endif

  return "labelChronological";
};

const syncScrollingForLayout = (layout, childViewTypes, scrollingLocked) =>
  scrollingLocked &&
  canViewTypeSyncScrolling(childViewTypes) &&
  layout.children.count() > 1 &&
  layout.orientation === ORIENTATION_ROW;

const calcSizeValueForLayout = (sizeParam, layout, children, childViewTypes, syncScrolling) => {
  const childCount = children.count();
  const relevantDimension = layout.orientation === ORIENTATION_ROW ? "height" : "width";
  const oppositeDimension = layout.orientation === ORIENTATION_COLUMN ? "height" : "width";

  const isHorizontalCollapsibleChildren = child =>
    layout.orientation === ORIENTATION_ROW && child.viewType !== LAYOUT_VIEW;

  const dimensionTotal =
    (childCount - 1) * SPLIT_DIVIDER_SIZE +
    children.reduce((sum, c) => {
      return (
        sum +
        // The min size of the horizontal work view
        // should be the collapsed height
        (sizeParam === "minSize" && isHorizontalCollapsibleChildren(c)
          ? Dimensions.COLLAPSE_WORKVIEW_HEIGHT
          : c.getIn([sizeParam, relevantDimension]))
      );
    }, 0) +
    // The cases of the sync scrolling views
    (syncScrolling && childViewTypes === TIMELINE_VIEW ? CONTEXT_BAR_HEIGHT + TIMELINE_HEADER_HEIGHT : 0) +
    (syncScrolling && childViewTypes === RELATIONSHIP_VIEW ? RELATIONSHIP_HEADER_HEIGHT : 0);

  const oppositeMax = children.reduce((max, c) => Math.max(max, c.getIn([sizeParam, oppositeDimension])), 0);

  const size = {};
  size[relevantDimension] = dimensionTotal;
  size[oppositeDimension] = oppositeMax;
  return createFromJS.SizeRecord(size);
};

const adjustZeroSizedChildren = (orientation, children) => {
  const childCount = children.count();
  const relevantDimension = orientation === ORIENTATION_ROW ? "height" : "width";
  const zeroCount = children.reduce((count, c) => count + (c.size[relevantDimension] === 0 ? 1 : 0), 0);
  const totalSum = Math.max(
    1,
    children.reduce((sum, c) => sum + c.size[relevantDimension], 0)
  );
  const percentAdjust = (childCount - zeroCount) / childCount;
  const result = children.map(c =>
    c.updateIn(["size", relevantDimension], s => (s === 0 ? totalSum / childCount : s * percentAdjust))
  );
  return result;
};

const getNestedViewsForLayout = (layout, viewsById, activeViewId, scrollingIsLocked) => {
  if (Record.isRecord(layout)) {
    const children = adjustZeroSizedChildren(
      layout.orientation,
      layout.children.map(l => getNestedViewsForLayout(l, viewsById, activeViewId, scrollingIsLocked))
    );

    let childViewTypes = children.reduce(
      (t, c) =>
        t
          ? c.viewType === t && scrollingIsLocked && layout.orientation === ORIENTATION_ROW
            ? t
            : MIXED_VIEWS
          : c.viewType,
      null
    );

    // Note: To avoid unnecessary layout re-rendering, don't change from MIXED_VIEWS unless we can actually sync the
    // scrolling.
    childViewTypes = canViewTypeSyncScrolling(childViewTypes) || children.count() === 1 ? childViewTypes : MIXED_VIEWS;

    const scrollSync = syncScrollingForLayout(layout, childViewTypes, scrollingIsLocked);
    const size = calcSizeValueForLayout("size", layout, children, childViewTypes, scrollSync);
    const minSize = calcSizeValueForLayout("minSize", layout, children, childViewTypes, scrollSync);
    return createFromJS.ViewLayoutDisplayRecord(layout).merge({
      children,
      size,
      minSize,
      scrollSync,
      childViewTypes
    });
  } else {
    // Not a map, so just the ID string.
    // Return a new ViewDisplayRecord created from the original ViewRecord
    // and add the extra values.
    // At some point, we may want to set different minSize values based on the type
    // of view, and perhaps whether it has to have its own header.
    const origView = viewsById.get(layout);
    return createFromJS.ViewDisplayRecord(origView).merge({
      isActive: layout === activeViewId,
      storedSize: origView.size
    });
  }
};

const getOpenViewIds = state =>
  getOpenViewsById(state)
    .map((v, k) => k)
    .toList();

const getCurrentViews = createSelector(
  // Get the required values
  getOpenViews,
  getActiveViewId,
  state => state.getIn(["appState", "scrollingIsLocked"]),
  // Final function using the prepared params
  (openViews, activeViewId, scrollingIsLocked) => {
    return getNestedViewsForLayout(openViews.get("layout"), openViews.get("byId"), activeViewId, scrollingIsLocked);
  }
);

const getActiveView = createSelector([getOpenViews, getActiveViewId], (views, activeViewId) => {
  return views.getIn(["byId", activeViewId]);
});

const getActiveViewType = createSelector([getActiveView], view => {
  return view.viewType;
});

const getActiveViewMindmapId = createSelector([getActiveView], view => {
  return view.viewType === MINDMAP_VIEW ? view.getIn(["mindmapOptions", "mindmapId"]) : null;
});

const getActiveViewZoom = state => {
  const view = getActiveView(state);
  const calendar = calendarSelectors.getCalendar(state);
  let minValue = 1;
  let maxValue = 1;
  let value = 1;
  if (view.viewType === TIMELINE_VIEW) {
    const viewSettings = definitionSelectors.getTimelineViewSettings(state);
    minValue = unitScaleForZoomLimit(calendar, viewSettings.zoomFrom, true);
    maxValue = unitScaleForZoomLimit(calendar, viewSettings.zoomTo, false);
    value = getViewUnitScale(state);
  }
  return {
    minValue,
    maxValue,
    value
  };
};

const getViewMindmapId = (state, viewId) => getOpenViewsById(state).getIn([viewId, "mindmapOptions", "mindmapId"]);
const getViewMindmapZoom = (state, viewId) => getOpenViewsById(state).getIn([viewId, "mindmapOptions", "zoom"]);
const getViewTypeForView = (state, viewId) => getOpenViewsById(state).getIn([viewId, "viewType"]);
const getViewRelationshipColumnIds = (state, viewId) =>
  getOpenViewsById(state).getIn([viewId, "relationshipOptions", "columnIds"]);
const getViewRelationshipTypeIds = (state, viewId) =>
  getOpenViewsById(state).getIn([viewId, "relationshipOptions", "typeIds"]);

const getViewRelationshipColumnIdsAsSet = createSelector(
  // input
  getViewRelationshipColumnIds,
  // output
  columnIds => columnIds.toSet()
);

const getViewSubwayThreadIds = (state, viewId) => getOpenViewsById(state).getIn([viewId, "subwayOptions", "threadIds"]);

const getTypeIdsForView = (state, viewId) => {
  const view = getOpenViewsById(state).get(viewId);
  return typeSelectors.getTypeIdsByViewType(state, view.viewType);
};

const getViewUnitScale = (state, viewId) => {
  // If viewId == -1, that means it applies to all.
  // So we might as well grab it from the first one!
  const v = viewId ? viewId : getActiveViewId(state);
  let scale = getOpenViewsById(state).getIn([v, "timelineOptions", "unitScale"]);

  if (!scale) {
    scale = definitionSelectors.getDefaultUnitScale(state);
  }
  return scale;
};

const getViewScrollPosition = (state, viewId) => {
  // If viewId == -1, that means it applies to all.
  // So we might as well grab it from the first one!
  const v = viewId ? viewId : getActiveViewId(state);
  return getOpenViewsById(state).getIn([v, "timelineOptions", "scrollPosition"]);
};

const getViewExpandedItems = (state, viewId) => {
  return getOpenViewsById(state).get(viewId).expandedItems;
};
const getViewExpandedItemsForMobile = state => state.appState.mobileView[getExpandedItemKeyForMobile(state)];

const getViewTimelineGroupIds = (state, viewId) =>
  getOpenViewsById(state).getIn([viewId, "timelineOptions", "groupIds"]);

const getTimelineOptionsForView = (state, viewId) => getOpenViewsById(state).get(viewId).timelineOptions;
const getRelationshipOptionsForView = (state, viewId) => getOpenViewsById(state).get(viewId).relationshipOptions;
const getMindmapOptionsForView = (state, viewId) => getOpenViewsById(state).get(viewId).mindmapOptions;
const getOutlineOptionsForView = (state, viewId) => getOpenViewsById(state).get(viewId).outlineOptions;
const getSpreadsheetOptionsForView = (state, viewId) => getOpenViewsById(state).get(viewId).spreadsheetOptions;
const getSubwayOptionsForView = (state, viewId) => getOpenViewsById(state).get(viewId).subwayOptions;

const getRelationshipViewSortBy = (_state, _viewId) => sortOptions.SEQUENCE;
const getOutlineViewSortBy = (_state, _viewId) => sortOptions.NARRATIVE;
const getSpreadsheetViewSortBy = (state, viewId) => getSpreadsheetOptionsForView(state, viewId).sortBy;
const getSpreadsheetViewSortInDesc = (state, viewId) => getSpreadsheetOptionsForView(state, viewId).sortInDesc;

const getSuggestionsForGroup = (suggestionGroup, definitions) => {
  return suggestionGroup.suggestions.allIds
    .map(i => {
      const s = suggestionGroup.suggestions.byId.get(i);
      const d = suggestionLibraryValues[s.id];
      if (!s || !d) {
        return null;
      }
      const label = d.createLabel(definitions);
      const subLabel = d.createSubLabel(definitions);
      return label && subLabel
        ? immutableRecords.SuggestionDisplayRecord({
          id: s.id,
          complete: s.complete,
          label: d.createLabel(definitions),
          subLabel: d.createSubLabel(definitions),
          createClickAction: d.createClickAction
        })
        : null;
    })
    .filter(s => s);
};

const getSuggestionGroups = createSelector(
  // Inputs
  state => state.appState.suggestionGroups,
  state => definitionSelectors.getEnabledDefinitions(state),
  // Calculation
  (suggestionGroups, definitions) =>
    suggestionGroups
      .map(g => {
        const groupLabel = suggestionLibrary[g.id].createLabel(definitions);
        return groupLabel
          ? immutableRecords.SuggestionGroupDisplayRecord({
            id: g.id,
            label: groupLabel,
            suggestions: getSuggestionsForGroup(g, definitions)
          })
          : null;
      })
      .filter(g => g)
);

const getIncompleteSuggestionsInGroup = suggestionGroup =>
  suggestionGroup.suggestions.byId.filter(s => !s.complete && suggestionLibraryValues[s.id]).size;

const getIncompleteSuggestionsCount = state =>
  state.appState.suggestionGroups.reduce((count, g) => count + getIncompleteSuggestionsInGroup(g), 0);

const getIncompleteSuggestionsInFirstGroup = state =>
  state.appState.suggestionGroups.reduce(
    (firstCount, g) => (firstCount ? firstCount : getIncompleteSuggestionsInGroup(g)),
    0
  );

const getFirstGroupWithIncompleteSuggestions = state =>
  state.appState.suggestionGroups.find(g => getIncompleteSuggestionsInGroup(g));

const getExpandedSyncItems = state =>
  state.appState.get("expandedSyncItems") ? state.appState.get("expandedSyncItems") : List();

/**
 * @param {TimelineRecord} state
 * @param {string} entityType
 */
const getExpandedEntityItems = createCachedSelector(
  (state, entityType) => state.appState.getIn(["expandedEntityItems", entityType]) || List(),
  expandedEntityItems => expandedEntityItems
)((_state, entityType) => entityType);

const getExpandedFilterGroups = (state, filterId) => state.appState.get("expandedFilterGroups").get(filterId) || List();

const getExcludedSearchTypes = state =>
  state.appState.get("excludeSearchTypes") ? state.appState.get("excludeSearchTypes") : List();

const getFontPercentage = state => (state.appState.get("fontPercentage") ? state.appState.get("fontPercentage") : 100);

const getFontSize = state => {
  const globalFontSize = state.preferences.get("globalFontSize");
  const fontPercentage = getFontPercentage(state);
  return Math.round(
    globalFontSize && fontPercentage ? (globalFontSize / 100) * fontPercentage : Dimensions.BASE_FONT_SIZE
  );
};

export default {
  getOpenViews,
  getOpenViewsById,
  getRootLayout,
  getCurrentViews,
  getActiveViewId,
  getActiveView,
  isViewActive,
  getOpenViewIds,
  getOpenViewCount,
  getActiveViewType,
  getActiveViewMindmapId,
  getViewLabel,
  getViewLabelColor,
  getViewSubLabel,
  getViewGroupLabel,
  getViewFilterLabel,
  getViewMindmapId,
  getViewMindmapZoom,
  getViewRelationshipColumnIds,
  getViewRelationshipColumnIdsAsSet,
  getViewRelationshipTypeIds,
  getViewSubwayThreadIds,
  getViewTimelineGroupIds,
  getViewTypeForView,
  getTypeIdsForView,
  getViewUnitScale,
  getViewScrollPosition,
  getScrollingIsLocked,
  getDisableFiltering,
  getDisableFilteringMore,
  getDisableTimelineZoom,
  getExposedFilterTypes,
  getDisableSplitView,
  getViewExpandedItems,
  getViewExpandedItemsForMobile,
  getTimelineOptionsForView,
  getRelationshipOptionsForView,
  getMindmapOptionsForView,
  getOutlineOptionsForView,
  getSpreadsheetOptionsForView,
  getSubwayOptionsForView,
  getActiveViewZoom,
  getGlobalFilter,
  getViewFilter,
  getOutlineViewSortBy,
  getSpreadsheetViewSortBy,
  getSpreadsheetViewSortInDesc,
  getRelationshipViewSortBy,
  getIgnoredNotifications,
  getDesktopLeftPanelMode,
  getTabletPanelMode,
  getMobilePanelMode,
  getLeftPanelMode,
  getShowRightPanel,
  getRightPanelMode,
  isItemInspectorOpen,
  isSyncTabOpen,
  getLeftPanelWidth,
  getRightPanelWidth,
  getDesktopVisibleTypeListId,
  getTabletVisibleTypeListId,
  getMobileVisibleTypeListId,
  getVisibleTypeListId,
  getInspectorTab,
  getNotificationTab,
  getSyncTab,
  getSelectedItems,
  getSelectedItemCount,
  getSelectedItemsShareSuper,
  getSelectedItemsShareNarrativeParent,
  getEarliestSharedNarrativePositionForSelectedItems,
  getSelectedItemsAreUnlocked,
  getSelectedMarkers,
  getSelectedMarkerCount,
  getSelectedSyncItems,
  getSelectedSyncItemCount,
  getSuggestionGroups,
  getIncompleteSuggestionsCount,
  getIncompleteSuggestionsInFirstGroup,
  getFirstGroupWithIncompleteSuggestions,
  getExpandedSyncItems,
  getExpandedFilterGroups,
  getExpandedEntityItems,
  getExcludedSearchTypes,
  canViewTypeSyncScrolling,

  getFontPercentage,
  getFontSize,

  // Mobile stuff
  getMobilePortraitViewType
};
